// =============================================================================
// src/views/workspace/workspace-route.ts — Rota full-bleed do Workspace (?view=workspace).
//
// Proveniencia: assets/unified-app.js WorkspaceRoute (linhas 2443-2532). Promove
//   o Workspace Pessoal a uma view dedicada com URL propria. Sincroniza com a URL
//   (?view=workspace), pausa o scroll-spy enquanto aberto, ativa o comportamento
//   da secao na primeira abertura e ajusta titulo/breadcrumb. Portado FIEL: o
//   guard de history.pushState (try/catch para file://), o aria-hidden, o
//   invalidateSize 50ms no fechamento e a chave de estado is-workspace-view.
//
// SRP: so o ciclo de vida da rota (URL + classe + titulo + pause). Os listeners
//   internos do Workspace ficam em workspace-section.ts; a marcacao em index.ts.
// =============================================================================

import type { AppContext, MapController } from '../../app/types';
import { qs } from '../../app/dom';
import { SectionManager } from '../../core/section-manager';

/** Classe no body que marca a rota aberta (paridade com o legado). */
const WORKSPACE_BODY_CLASS = 'is-workspace-view';
/** Titulo do documento enquanto a rota esta aberta. */
const WORKSPACE_OPEN_TITLE = 'Workspace Pessoal · Mapa Político';
/** Titulo default de fallback (igual ao legado quando defaultTitle nao foi capturado). */
const WORKSPACE_FALLBACK_TITLE = 'Mapa Político · Manaus';
/** Rotulo do breadcrumb enquanto a rota esta aberta. */
const WORKSPACE_CRUMB = 'Workspace Pessoal';
/** Ms ate revalidar o tamanho do mapa apos fechar (espera o layout reflow). */
const WORKSPACE_INVALIDATE_MS = 50;

// Aba Workspace desativada em 2026-07-25 (pedido do usuario, pacote de ajustes do
// Analytics). Flag unico para religar o deep-link ?view=workspace no syncFromURL.
const WORKSPACE_ROUTE_DISABLED = true;

/**
 * Globais legados ainda nao portados como modulos TS. WorkspaceRoute os consulta
 * defensivamente (mesmos `typeof ... !== 'undefined'` do original): GrafoRoute
 * para nao restaurar o breadcrumb por cima de uma rota grafo aberta; TopbarNav
 * para re-sincronizar as pills da topbar.
 */
interface OptionalRouteNeighbors {
  GrafoRoute?: { isOpen(): boolean };
  TopbarNav?: { syncActive(): void };
}

/**
 * Controlador da rota Workspace. Singleton exportado ao final; main.ts chama
 * WorkspaceRoute.init() apos montar o #routeLayer. O MapController e injetado em
 * init() (DI) em vez do global legado.
 */
class WorkspaceRouteController {
  private page: HTMLElement | null = null;
  private ready = false;
  private defaultTitle: string | null = null;
  private mapCtrl: MapController | null = null;
  private context: AppContext | null = null;
  private activateSectionFn: ((ctx: AppContext) => void) | null = null;

  /**
   * Liga a rota: localiza a pagina, captura o titulo default, liga o botao voltar,
   * Escape para fechar, popstate e sincroniza com a URL inicial. Recebe o AppContext
   * (DI: bus/commands/map) e o activator da secao injetado — mesmo padrao do Grafo
   * (grafo-route.ts), evitando o import estatico de workspace-section (sem ciclo).
   */
  init(ctx: AppContext, activateSection: (ctx: AppContext) => void): void {
    this.page = qs<HTMLElement>('#u-workspace-page');
    if (!this.page) return;
    this.context = ctx;
    this.activateSectionFn = activateSection;
    this.mapCtrl = ctx.map;
    this.defaultTitle = document.title;
    this.bindControls();
    this.syncFromURL();
  }

  /** Liga voltar, Escape e popstate (avatar/chip Manaus ficam a cargo do TopbarNav). */
  private bindControls(): void {
    const backBtn = qs<HTMLElement>('#u-workspace-page-back');
    if (backBtn) backBtn.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) this.close();
    });
    window.addEventListener('popstate', () => this.syncFromURL());
  }

  /** Verdadeiro enquanto a rota esta aberta (classe no body). */
  isOpen(): boolean {
    return document.body.classList.contains(WORKSPACE_BODY_CLASS);
  }

  /** Alterna abrir/fechar (mesmo helper do legado). */
  toggle(): void {
    if (this.isOpen()) this.close();
    else this.open();
  }

  /** Abre a rota, empurrando ?view=workspace no historico (tolerante a file://). */
  open(): void {
    if (this.isOpen()) return;
    this.pushHistory('?view=workspace', { view: 'workspace' });
    this.apply(true);
  }

  /** Fecha a rota, limpando a query string (tolerante a file://). */
  close(): void {
    if (!this.isOpen()) return;
    this.pushHistory(window.location.pathname, {});
    this.apply(false);
  }

  /** history.pushState guardado (file:// ou sandbox lancam — ignorar, como o legado). */
  private pushHistory(url: string, state: Record<string, string>): void {
    try {
      history.pushState(state, '', url);
    } catch {
      /* file:// ou sandboxed: ignora (paridade com o try/catch legado) */
    }
  }

  /** Aplica o estado da URL atual a rota (handler de popstate/boot). */
  syncFromURL(): void {
    // Workspace DESATIVADO (2026-07-25, pacote de ajustes do Analytics): sem este
    // guard, ?view=workspace reabriria a rota por fora do pill disabled — a mesma
    // porta dos fundos que motivou o corte de bootstrap do Grafo em 2026-07-20.
    // Para religar: voltar o predicado para params.get('view') === 'workspace'
    // (e reverter landing-view.ts + DISABLED_PILL_VIEWS + pill/palette).
    if (WORKSPACE_ROUTE_DISABLED) {
      this.apply(false);
      return;
    }
    const params = new URLSearchParams(window.location.search);
    this.apply(params.get('view') === 'workspace');
  }

  /**
   * Liga/desliga a rota: classe no body, aria-hidden, titulo, breadcrumb, ativacao
   * tardia do comportamento, pause do scroll-spy, invalidateSize ao fechar e
   * re-sync da topbar. Espelha apply(on) do legado, passo a passo.
   */
  apply(on: boolean): void {
    document.body.classList.toggle(WORKSPACE_BODY_CLASS, on);
    if (this.page) this.page.setAttribute('aria-hidden', on ? 'false' : 'true');
    document.title = on ? WORKSPACE_OPEN_TITLE : (this.defaultTitle ?? WORKSPACE_FALLBACK_TITLE);
    this.applyBreadcrumb(on);
    this.activateOnce(on);
    SectionManager.setPaused(on);
    this.invalidateMapOnClose(on);
    this.syncTopbar();
  }

  /** Define o breadcrumb: 'Workspace Pessoal' aberto; restaura a secao ativa ao fechar. */
  private applyBreadcrumb(on: boolean): void {
    const crumb = qs('#breadcrumbCurrent');
    if (!crumb) return;
    if (on) {
      crumb.textContent = WORKSPACE_CRUMB;
      return;
    }
    this.restoreSectionBreadcrumb();
  }

  /**
   * Restaura o breadcrumb da secao ativa ao fechar (replica o ramo `else` do
   * legado que chamava SectionManager.updateBreadcrumb(activeSection)). Como
   * updateBreadcrumb e privado no SectionManager TS, re-ativamos a secao ativa:
   * activateSection sincroniza o breadcrumb internamente (mesmo resultado visivel)
   * e e seguro pois a secao ativa nao mudou enquanto a rota esteve aberta.
   * Pula quando uma rota grafo legada esta aberta (guard original).
   */
  private restoreSectionBreadcrumb(): void {
    const neighbors = window as unknown as OptionalRouteNeighbors;
    if (neighbors.GrafoRoute?.isOpen()) return;
    const active = SectionManager.activeSection;
    if (active) SectionManager.activateSection(active);
  }

  /** Ativa o comportamento do Workspace na primeira abertura (lazy, como o legado). */
  private activateOnce(on: boolean): void {
    if (!on || this.ready) return;
    const ctx = this.context;
    const activate = this.activateSectionFn;
    /* v8 ignore next -- init liga context+activateSectionFn juntos; null aqui é estado impossível */
    if (!ctx || !activate) return;
    activate(ctx);
    this.ready = true;
  }

  /** Revalida o tamanho do mapa apos fechar (espera o reflow do layout). */
  private invalidateMapOnClose(on: boolean): void {
    if (on || !this.mapCtrl) return;
    setTimeout(() => this.mapCtrl?.invalidateSize(), WORKSPACE_INVALIDATE_MS);
  }

  /** Re-sincroniza as pills da topbar, se o TopbarNav legado/portado estiver presente. */
  private syncTopbar(): void {
    const neighbors = window as unknown as OptionalRouteNeighbors;
    neighbors.TopbarNav?.syncActive();
  }
}

/**
 * Singleton da rota Workspace — substitui o `var WorkspaceRoute` legado. main.ts
 * injeta o MapController em init(map) apos montar o DOM.
 */
export const WorkspaceRoute = new WorkspaceRouteController();
